import { z } from "zod";

export const ReviewableProjectSchema = z.object({
    id: z.number(),
    name: z.string(),
    status: z.string(),
    has_review: z.boolean(),
    has_pending_review_request: z.boolean(),
    can_request_review: z.boolean(),
    review_invitation_sent_at: z.string().nullable().optional(),
});

export const ReviewableProjectsResponseSchema = z.object({
    success: z.literal(true),
    data: z.array(ReviewableProjectSchema),
    pagination: z.object({ current_page: z.number(), last_page: z.number() }),
});

// The project's own invitation email (automatic or sent manually), not a personal invite.
export const ProjectReviewRequestStateSchema = z.object({
    status: z.literal("sent"),
    sent_at: z.string(),
    reminder_sent_at: z.string().nullable(),
});

export const SendProjectReviewRequestResponseSchema = z.object({
    review_request: ProjectReviewRequestStateSchema,
});

export const ProjectReviewStatusSchema = z.object({
    project_id: z.number(),
    has_review: z.boolean(),
    can_submit_review: z.boolean(),
    show_review_banner: z.boolean(),
    can_edit_review: z.boolean().optional(),
    can_reply: z.boolean().optional(),
    reply_requires_pro: z.boolean().optional(),
    // Defaults keep older API responses valid during a rolling deploy.
    auto_review_requests_enabled: z.boolean().default(true),
    auto_prompt_review: z.boolean().default(true),
    review_request: ProjectReviewRequestStateSchema.nullable().default(null),
    can_send_review_request: z.boolean().default(false),
    review: z.object({
        id: z.number(),
        status: z.string(),
        is_anonymous: z.boolean().optional(),
        review_response: z.object({ response_text: z.string(), created_at: z.string().nullish() }).nullish(),
        quality: z.number(),
        communication: z.number(),
        timelines: z.number(),
        value_for_money: z.number(),
        overall_rating: z.number(),
        review_text: z.string(),
        created_at: z.string(),
    }).nullable(),
});

export const UpdateProjectReviewSettingsSchema = z.object({
    acting_org_id: z.number().int().positive(),
    auto_review_requests_enabled: z.boolean(),
});
export type UpdateProjectReviewSettingsBody = z.infer<typeof UpdateProjectReviewSettingsSchema>;

export const ProjectReviewSettingsSchema = z.object({
    auto_review_requests_enabled: z.boolean(),
});

export const SubmitProjectReviewSchema = z.object({
    quality: z.number().int().min(1).max(5),
    communication: z.number().int().min(1).max(5),
    timelines: z.number().int().min(1).max(5),
    value_for_money: z.number().int().min(1).max(5),
    review_text: z.string().min(10).max(5000),
    is_anonymous: z.boolean().optional(),
});
export type ProjectReviewStatus = z.infer<typeof ProjectReviewStatusSchema>;
export type SubmitProjectReviewBody = z.infer<typeof SubmitProjectReviewSchema>;
